import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Loading } from '@nextui-org/react';
import { mstore } from "../../store";
import checkAccess from '../../lib/checkAccess';
import endpoint from "../../config/endpoint.json";
import { error } from "../../lib/userAlerts";
import { fetchWithJson } from "../../lib/api";
import FeatureSelection from "./FeatureSelection";

const FeatureManagement = () => {
  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  const access = useSelector(state => state.navigation.Access);

  const [state, setState] = useState({
    fetching: true,
    rawfeatureList: [],
    featureList: {},
    pageText: {}
  });

  const sortFeaturesList = (rawList) => {
    const filteredList = rawList.reduce((acc, item) => {
      const category = item.cat_desc;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});

    setState(prev => ({ ...prev, featureList: filteredList }));
  };

  const fetchFeatureList = async () => {
    setState(prev => ({ ...prev, fetching: true }));
    try {
      const response = await fetchWithJson(endpoint.settingsProductFeatures, {
        company_id: clientId,
      });
      const data = await response.json();
      setState(prev => ({ ...prev, rawfeatureList: data }));
      sortFeaturesList(data);
    } catch (err) {
      error("Unable to fetch feature list");
      console.error(err);
    } finally {
      setState(prev => ({ ...prev, fetching: false }));
      checkAccess(clientId);
    }
  };

  const fetchPageText = async () => {
    try {
      const response = await fetchWithJson(endpoint.subscriptionPageText);
      const data = await response.json();
      setState(prev => ({ ...prev, pageText: data }));
    } catch (err) {
      error("Unable to fetch page header text");
      console.error(err);
    }
  };

  const settingsFeatureUpdate = async (obj) => {
    setState(prev => ({ ...prev, fetching: true }));
    try {
      await fetchWithJson(endpoint.settingsFeatureUpdate, {
        company_id: clientId,
        feature_id: obj.feature_id,
        is_enabled: obj.is_feature_active === 1 ? 0 : 1,
      });
      await fetchFeatureList();
    } catch (err) {
      console.error(err);
      setState(prev => ({ ...prev, fetching: false }));
    }
  };

  const handleSwitch = (obj) => {
    // Validation checks
    if (obj.feature_id === 1004 && obj.is_feature_active === 1 && access.allow_osd === 1) {
      error("Please turn off the OS&D feature before disabling the Shipment documents feature.");
      return;
    }

    if (obj.feature_id === 1007 && obj.is_feature_active === 0 && access.allow_artifax === 0) {
      error("Please turn on the documents setting feature before enabling the OS&D feature.");
      return;
    }

    if (obj.feature_id === 1013 && obj.is_feature_active === 0 && access.allow_broker_shipment_management === 0) {
      error("Please enable shipment management features first. GPS tracking cannot be enabled when shipment management is disabled.");
      return;
    }

    if (obj.feature_id === 1016 && obj.is_feature_active === 1 && access.allow_broker_carrier_driver_gps === 1) {
      error("Please disable GPS tracking features first. GPS tracking cannot remain enabled when shipment management is disabled.");
      return;
    }

    settingsFeatureUpdate(obj);
  };

  useEffect(() => {
    fetchFeatureList();
    fetchPageText();
  }, [clientId]);

  if (state.fetching) {
    return (
      <Loading className='p-32' size='lg'>Fetching feature list</Loading>
    );
  }

  return (
    <div className="p-16 bg-gray-100">
      <div className='card w-full bg-base-100 shadow-lg'>
        <div className="card-body">
          <div className='pb-4 text-2xl font-semibold'>{state.pageText?.sub_page_title}</div>
          <p className='text-slate-800'>{state.pageText?.sub_page_details}</p>
        </div>
      </div>
      {Object.entries(state.featureList).map(([key, features]) => (
        <div key={key} className='pt-8'>
          <div className='pb-4 text-2xl font-semibold'>{key}</div>
          <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4 pb-4">
            <FeatureSelection
              featureList={features}
              handleSwitch={handleSwitch}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default FeatureManagement;
 